import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  ArrowRight,
  ArrowSquareOut,
  CalendarBlank,
  Check,
  Clock,
  Copy,
  Eye,
  File,
  FolderOpen,
  PencilSimple,
  PlayCircle,
  PresentationChart,
  SpinnerGap,
  SquaresFour,
  VideoCamera,
} from './ui/icons'
import { PricingCalculatorV2 } from './PricingCalculatorV2'
import { CaseStudiesTab, ProductTab, SandboxRequestCard } from './WorkspaceLibrary'
import { QuoteHistory } from './PricingCalculator'
import { calculateEnrollmentRoi, DEFAULT_ENROLLMENT_INPUT, type EnrollmentRoiInput } from '../calculators/enrollment'
import { calculateLicensePricing, DEFAULT_LICENSE_INPUT, type LicensePricingInput } from '../calculators/license'
import { adaptSavedInputToPricingV2, allowedPricingModes, applyCalculatorRules, computeFullPricingV2Quote, isPricingV2SavedInput, PRICING_V2_INPUT_VERSION, type PricingMode, type PricingV2Input } from '../calculators/pricing-v2'
import { compactMoney, initials, money, number, shortDate, unitMoney } from '../lib/format'
import { licensesPerYearPhrase, licenseTermPhrase, termPhrase } from '../lib/license-wording'
import { applyHouseNoun, houseNoun } from '../lib/house-noun'
import { api, type OrderFormInvoiceLinks } from '../lib/api'
import { normalizeRenewal } from '../lib/workspace-patch'
import type { AdminWorkspace, CustomerTab, OrderForm, ProjectTask, PublicWorkspace, RenewalOption, WorkspaceAsset, WorkspaceProject, WorkspaceQuote, WorkspaceTask } from '../types'
import { OrderFormDocument } from './OrderFormDocument'
import { ResponsePlanTab } from './ResponsePlanTab'
import { formatDisplayDate } from './ui/date-logic'
import { billingContactLine, customerDetails, customerEditBlocked, customerVisibleOrderForm, type CustomerDetails, ORDER_FORM_CUSTOMER_STAGE_LABEL, ORDER_FORM_STAGE_BADGE, ORDER_FORM_STAGE_LABEL, orderFormCustomerSigner, orderFormStage, orderFormTotals, signedDocuments, viewerSigningUrl } from '../lib/order-form'
import { DocusealForm } from '@docuseal/react'
import { lineAmount as lineTotal, optionTotals } from '../lib/proposal-math'
import { SectionHeader } from './ui/primitives'
import { Alert, Button, Dialog, Field, StatusBadge, useToast } from './ui'
import { logosForSegment } from '../data/customer-logos'

const SALES_TABS: Array<{ key: CustomerTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'product', label: 'Product' },
  { key: 'case-studies', label: 'Case Studies' },
  { key: 'pricing', label: 'ROI & Pricing' },
  { key: 'renewal', label: 'Proposal' },
  { key: 'order-form', label: 'Order Form' },
]

const PHASE_TABS: Record<PublicWorkspace['phase'], Array<{ key: CustomerTab; label: string }>> = {
  sales: SALES_TABS,
  onboarding: [
    { key: 'overview', label: 'Overview' },
    { key: 'implementation', label: 'Implementation' },
    { key: 'training', label: 'Training' },
    { key: 'product', label: 'Product' },
    { key: 'renewal', label: 'Proposal' },
    { key: 'order-form', label: 'Order Form' },
  ],
  customer_success: [
    { key: 'overview', label: 'Overview' },
    { key: 'projects', label: 'Projects' },
    { key: 'renewal', label: 'Renewal' },
    { key: 'order-form', label: 'Order Form' },
    { key: 'pricing', label: 'ROI & Pricing' },
    { key: 'calls', label: 'Calls' },
    { key: 'whats-new', label: "What's New" },
    { key: 'resources', label: 'Resources' },
  ],
}

interface WorkspaceExperienceProps {
  workspace: PublicWorkspace | AdminWorkspace
  adminMode?: boolean
  /** False for local fixtures, which have no Worker to render the PDF packet; the HTML rendering shows instead. */
  liveDocuments?: boolean
  onEdit?: () => void
  onSavePricing?: (inputs: Record<string, unknown>, output: Record<string, unknown>) => void
  onTrack?: (eventType: string, targetId?: string) => void
  onRequestSandbox?: (context: string) => Promise<void>
  onGenerateQuote?: (inputs: Record<string, unknown>) => Promise<WorkspaceQuote>
  /** Reloads the workspace (customer view): the Order Form tab polls it after a signature. */
  onRefresh?: () => Promise<void>
  /** Customer view only: reports that the Order Form tab opened a form out for signature, or that the signer started signing. */
  onOrderFormActivity?: OrderFormActivityReporter
}

export type OrderFormActivityReporter = (formId: string, type: 'viewed' | 'signing_started') => void

/** Adds download=1, which the Worker counts as a customer download. */
function downloadUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}download=1`
}

/** The pricing modes this workspace's calculator offers: the admin setting, else Unlimited for universities only. */
function workspaceAllowedPricingModes(workspace: Pick<PublicWorkspace, 'pricingModes' | 'customerSegment' | 'calculatorInputs'>): PricingMode[] {
  return allowedPricingModes(workspace.pricingModes, { customerSegment: workspace.customerSegment, calculatorInputs: workspace.calculatorInputs })
}

/**
 * How the Overview uses the pre-call one-pager (Rod, 2026-10-26):
 * - 'prequalification': no deal yet, so the one-pager is the whole Overview. Unchanged.
 * - 'precall': the one-pager sections lead the regular Overview until the first call.
 * - 'none': no enabled one-pager, or the first customer call is recorded. The data stays; it only stops rendering.
 */
export function overviewOnepagerMode(workspace: Pick<PublicWorkspace, 'precallOnepager' | 'prequalificationOnly' | 'firstCallRecorded'>): 'prequalification' | 'precall' | 'none' {
  if (!workspace.precallOnepager?.enabled) return 'none'
  if (workspace.prequalificationOnly) return 'prequalification'
  return workspace.firstCallRecorded ? 'none' : 'precall'
}

/**
 * Whether the customer can see an asset. The public DTO already holds only
 * published assets; the admin DTO marks each one. The admin Customer preview
 * filters with this so it shows exactly what the customer sees.
 */
function customerVisibleAsset(asset: WorkspaceAsset): boolean {
  return asset.visibility === undefined || asset.visibility === 'published'
}

/** The call recordings the Calls tab lists, newest first. */
export function callRecordings(assets: WorkspaceAsset[]): WorkspaceAsset[] {
  return assets
    .filter((asset) => asset.sectionKey === 'recordings' && customerVisibleAsset(asset))
    .sort((a, b) => recordingTime(b) - recordingTime(a))
}

/**
 * The assets a lifecycle tab (Implementation, Resources, What's New, ...) lists.
 * Resources is a global page: identical for every community, standard library
 * content only. Client-specific files stay out of it.
 */
export function lifecycleTabAssets(assets: WorkspaceAsset[], tab: CustomerTab): WorkspaceAsset[] {
  return assets.filter((asset) => customerVisibleAsset(asset) && asset.sectionKey === tab && (tab !== 'resources' || asset.source === 'standard'))
}

/**
 * The projects the customer can see. The customer DTO already drops drafts;
 * the admin DTO keeps them, so Customer preview filters the same way.
 */
export function customerVisibleProjects(projects: WorkspaceProject[] | undefined): WorkspaceProject[] {
  return (projects || []).filter((project) => !('visibility' in project) || project.visibility === 'published')
}

/** The documents attached to one project, as the Projects tab lists them. */
function projectDocuments(assets: WorkspaceAsset[], projectId: string): WorkspaceAsset[] {
  return assets.filter((asset) => asset.sectionKey === 'projects' && asset.metadata?.projectId === projectId && asset.visibility !== 'hidden')
}

/**
 * The documents Prentus made for this customer, for the Customer Success
 * Overview: the documents on each visible project, in project order, with the
 * project name as a label. Client-specific collateral (sectionKey
 * 'collateral') stays out: Customer Success has no tab or editor for it, so
 * sales-phase files would show with no way to manage them.
 */
export function sharedWithYouDocuments(workspace: Pick<PublicWorkspace | AdminWorkspace, 'projects' | 'assets'>): Array<{ asset: WorkspaceAsset; projectName: string }> {
  return customerVisibleProjects(workspace.projects).flatMap((project) =>
    projectDocuments(workspace.assets || [], project.id).map((asset) => ({ asset, projectName: project.name })))
}

/** The next open step on a project: the earliest due date first, then undated steps in plan order. */
export function nextOpenProjectTask(tasks: ProjectTask[]): ProjectTask | null {
  const open = tasks.filter((task) => task.status !== 'complete')
  const dated = open.filter((task) => task.dueDate).sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))
  return dated[0] || open[0] || null
}

/** The tab list a customer sees: the phase tabs, plus Response Plan right after Overview whenever a plan exists. */
export function visibleWorkspaceTabs(workspace: Pick<PublicWorkspace | AdminWorkspace, 'prequalificationOnly' | 'pricingModel' | 'phase' | 'projects' | 'renewal'> & { orderForms?: OrderForm[]; pricingLocked?: boolean; assets?: WorkspaceAsset[]; responsePlan?: PublicWorkspace['responsePlan'] }): Array<{ key: CustomerTab; label: string }> {
  // A response plan is its own tab, right after Overview, in every phase and
  // whatever the deal state. It does not change the workspace phase.
  return withResponsePlanTab(phaseTabs(workspace), Boolean(workspace.responsePlan))
}

function withResponsePlanTab(tabs: Array<{ key: CustomerTab; label: string }>, hasPlan: boolean): Array<{ key: CustomerTab; label: string }> {
  if (!hasPlan) return tabs
  const overview = tabs.findIndex((tab) => tab.key === 'overview')
  const planTab = { key: 'response-plan' as const, label: 'Response Plan' }
  return [...tabs.slice(0, overview + 1), planTab, ...tabs.slice(overview + 1)]
}

/**
 * The phase tabs. Pre-demo workspaces (prequalificationOnly) get Overview,
 * Product, and Case Studies, plus ROI & Pricing once a pricing model has been
 * made visible to them - the customer should be able to see pricing during a
 * demo even before a deal exists.
 */
function phaseTabs(workspace: Pick<PublicWorkspace | AdminWorkspace, 'prequalificationOnly' | 'pricingModel' | 'phase' | 'projects' | 'renewal'> & { orderForms?: OrderForm[]; pricingLocked?: boolean; assets?: WorkspaceAsset[] }): Array<{ key: CustomerTab; label: string }> {
  // A pricing lock keeps the tab for the customer's quotes, without the calculator.
  const showPricing = Boolean(workspace.pricingModel || workspace.pricingLocked)
  const pricingLabel = workspace.pricingModel ? 'ROI & Pricing' : 'Pricing'
  if (workspace.prequalificationOnly) {
    return [
      { key: 'overview', label: 'Overview' },
      { key: 'product', label: 'Product' },
      { key: 'case-studies', label: 'Case Studies' },
      ...(showPricing ? [{ key: 'pricing' as const, label: pricingLabel }] : []),
    ]
  }
  const hasVisibleProjects = customerVisibleProjects(workspace.projects).length > 0
  const assets = workspace.assets || []
  // Customer Success hides Calls, What's New and Resources until they have
  // content, so no tab shows only an empty state (Rod, 2026-10-29).
  const emptySuccessTab = (key: CustomerTab) => workspace.phase === 'customer_success' && (
    key === 'calls' ? callRecordings(assets).length === 0
      : key === 'whats-new' || key === 'resources' ? lifecycleTabAssets(assets, key).length === 0
        : false
  )
  return PHASE_TABS[workspace.phase]
    .filter((tab) => tab.key !== 'pricing' || showPricing)
    .map((tab) => tab.key === 'pricing' ? { ...tab, label: pricingLabel } : tab)
    .filter((tab) => tab.key !== 'projects' || hasVisibleProjects)
    .filter((tab) => tab.key !== 'renewal' || Boolean(normalizeRenewal(workspace.renewal)))
    .filter((tab) => tab.key !== 'order-form' || (workspace.orderForms || []).some(customerVisibleOrderForm))
    .filter((tab) => !emptySuccessTab(tab.key))
}

export function WorkspaceExperience({ workspace, adminMode = false, liveDocuments = true, onEdit, onSavePricing, onTrack, onRequestSandbox, onGenerateQuote, onRefresh, onOrderFormActivity }: WorkspaceExperienceProps) {
  const [activeTab, setActiveTab] = useState<CustomerTab>('overview')
  const visibleTabs = visibleWorkspaceTabs(workspace)

  useEffect(() => {
    const fromHash = window.location.hash.replace('#', '') as CustomerTab
    if (visibleTabs.some((tab) => tab.key === fromHash)) setActiveTab(fromHash)
    else setActiveTab('overview')
  }, [workspace.publicId])

  function selectTab(tab: CustomerTab) {
    setActiveTab(tab)
    window.history.replaceState(null, '', `#${tab}`)
    onTrack?.('tab_view', tab)
  }

  return (
    <main className="workspace-page">
      {adminMode && (
        <div className="admin-preview-bar">
          <div><strong>Customer preview</strong><span>This is exactly what the customer can see.</span></div>
          <button type="button" className="button button-secondary compact" onClick={onEdit}>Return to editor</button>
        </div>
      )}
      <div className="workspace-container">
        <header className="workspace-brand">
          <div className="brand-lockup">
            <img src="/prentus-logo-black.png" alt="Prentus" className="prentus-wordmark" />
            <span aria-hidden="true">×</span>
            {workspace.accountLogoUrl
              ? <img src={workspace.accountLogoUrl} alt={`${workspace.accountName} logo`} className="account-logo" />
              : <strong>{workspace.accountName}</strong>}
          </div>
          <div className="workspace-heading">
            <div>
              <h1>{workspace.phase === 'customer_success' ? 'Partnership Portal' : `From enrollment to employment at ${workspace.accountName}`}</h1>
              {workspace.phase !== 'customer_success' && <p>{workspace.overview}</p>}
            </div>
            {/* Customer Success shows the focus only when Prentus set one; no generic fallback. */}
            {(workspace.phase !== 'customer_success' || workspace.currentNextStep?.trim()) && (
              <aside className="next-action">
                <ArrowRight size={18} weight="bold" aria-hidden="true" />
                <div><span>{workspace.phase === 'customer_success' ? 'Current focus' : 'Current next step'}</span><strong>{workspace.currentNextStep || 'Confirm the next conversation and who should be involved.'}</strong></div>
              </aside>
            )}
          </div>
        </header>

        {visibleTabs.length > 1 && (
          <nav className="workspace-tabs" aria-label="Workspace sections">
            {visibleTabs.map((tab) => (
              <button type="button" key={tab.key} className={activeTab === tab.key ? 'active' : ''} aria-current={activeTab === tab.key ? 'page' : undefined} onClick={() => selectTab(tab.key)}>{tab.label}</button>
            ))}
          </nav>
        )}

        <div className="workspace-content" key={activeTab}>
          {activeTab === 'overview' && (workspace.phase === 'customer_success'
            ? <SuccessOverviewTab workspace={workspace} onNavigate={selectTab} onTrack={onTrack} />
            : <OverviewTab workspace={workspace} onNavigate={selectTab} onTrack={onTrack} />)}
          {activeTab === 'product' && (
            <ProductTab
              assets={workspace.assets}
              segment={workspace.customerSegment}
              sandbox={{ key: workspace.publicId, load: () => api.loadSandboxAccess(workspace.publicId), fallback: <SandboxRequestCard onRequest={onRequestSandbox} /> }}
              onTrack={onTrack}
            />
          )}
          {activeTab === 'case-studies' && <CaseStudiesTab assets={workspace.assets} segment={workspace.customerSegment} onTrack={onTrack} />}
          {activeTab === 'pricing' && !workspace.pricingModel && workspace.pricingLocked && <LockedPricingPanel workspace={workspace} onNavigate={selectTab} />}
          {activeTab === 'pricing' && workspace.pricingModel && (
            <div className="pricing-tab-stack">
              {adminMode && workspace.pricingLocked && (
                <Alert tone="info" title="Customer Cannot See the Calculator" description="This workspace has a Prentus quote or a sent or signed order form, so the customer sees those instead. You can still use the calculator here." />
              )}
              <PricingCalculatorV2
                initialInputs={adaptSavedInputToPricingV2(workspace.pricingModel, workspace.calculatorInputs)}
                allowedModes={workspaceAllowedPricingModes(workspace)}
                // The value block sits under the price summary, so a buyer sees value next to price.
                // It reads the live inputs, so it always states the same licenses and price as the quote.
                valueSummary={(input) => <ValueOpportunitySnapshot workspace={workspace} onNavigate={selectTab} showReviewLink={false} liveInput={input} compact />}
                onSave={onSavePricing
                  ? (inputs: PricingV2Input) => onSavePricing(
                      { ...inputs, formulaVersion: PRICING_V2_INPUT_VERSION },
                      computeFullPricingV2Quote(inputs) as unknown as Record<string, unknown>,
                    )
                  : undefined}
                onGenerateQuote={onGenerateQuote
                  ? (inputs: PricingV2Input) => onGenerateQuote({ ...inputs, formulaVersion: PRICING_V2_INPUT_VERSION })
                  : undefined}
                quotes={workspace.quotes || []}
              />
            </div>
          )}
          {activeTab === 'response-plan' && workspace.responsePlan && <ResponsePlanTab publicId={workspace.publicId} updatedAt={workspace.responsePlan.updatedAt} />}
          {activeTab === 'calls' && <CallsTab workspace={workspace} onTrack={onTrack} />}
          {activeTab === 'projects' && <ProjectsTab workspace={workspace} onTrack={onTrack} />}
          {activeTab === 'renewal' && normalizeRenewal(workspace.renewal) && <RenewalTab workspace={workspace} renewal={normalizeRenewal(workspace.renewal)!} onTrack={onTrack} />}
          {activeTab === 'order-form' && <OrderFormTab workspace={workspace} adminMode={adminMode} liveDocuments={liveDocuments} onTrack={onTrack} onActivity={adminMode ? undefined : onOrderFormActivity} onRefresh={adminMode ? undefined : onRefresh} onSaveDetails={adminMode || !onRefresh ? undefined : async (formId, details) => {
            const result = await api.updateOrderFormDetails(workspace.publicId, formId, details)
            if (result.changed) await onRefresh()
            return { changed: result.changed }
          }} />}
          {['implementation', 'training', 'adoption', 'outcomes', 'resources', 'support', 'whats-new'].includes(activeTab) && <LifecycleTab workspace={workspace} tab={activeTab} onTrack={onTrack} />}
        </div>

        <footer className="workspace-footer">
          {workspace.phase !== 'customer_success' && <img src="/prentus-logo-black.png" alt="Prentus" className="prentus-wordmark" />}
          <p>Private partnership workspace for {workspace.accountName}</p>
        </footer>
      </div>
    </main>
  )
}

/**
 * What the customer sees on the pricing tab once a quote or an order form
 * locks the calculator: their quotes, or a short note when there are none.
 */
function LockedPricingPanel({ workspace, onNavigate }: { workspace: PublicWorkspace | AdminWorkspace; onNavigate: (tab: CustomerTab) => void }) {
  const quotes = workspace.quotes || []
  if (quotes.length > 0) {
    return (
      <section className="quote-generator-card">
        <SectionHeader title="Your Quotes" description="Your pricing is in your quote. The final Order Form controls." />
        <QuoteHistory quotes={quotes} label="Recent quotes" />
      </section>
    )
  }
  if ((workspace.orderForms || []).some(customerVisibleOrderForm)) {
    return <Alert tone="info" title="Your Pricing Is in Your Order Form" action={<Button type="button" variant="secondary" compact onClick={() => onNavigate('order-form')}>Open Order Form</Button>} />
  }
  return <Alert tone="info" title="Your Pricing Is Being Finalized" description="Your Prentus team is preparing your Order Form. Your pricing will be in it." />
}

/**
 * The compact shared-value-v1 opportunity card: a "Current baseline"/"Current model" column
 * plus an "Estimated ... opportunity/value" column, computed from calculateEnrollmentRoi /
 * calculateLicensePricing. Used on Overview and, on the ROI & Pricing tab, directly under the
 * price summary (PricingCalculatorV2's valueSummary slot, with the live calculator inputs, Rod
 * 2026-10-26). The license branch states the quantity as the Pricing tab does: the term total
 * and the term ("2,500 licenses over 2 years, 1,250 a year"). Guarded with isPricingV2SavedInput: once a workspace
 * has saved v2-shaped calculatorInputs, spreading them into the v1 EnrollmentRoiInput/
 * LicensePricingInput defaults would silently corrupt fields like selectedModules (v2 and v1 use
 * different module ids) rather than fail loudly, so a v2-shaped input falls back to this
 * pricing model's own v1 defaults instead of reading the stored values.
 *
 * The license branch (round 8, 2026-09-25) still runs calculateLicensePricing for its itemized
 * value levers, but sizes the license count and reports the ROI multiple against the live v2
 * quote (computeFullPricingV2Quote via adaptSavedInputToPricingV2) rather than v1's own legacy
 * annualSubscription, so the ratio shown always matches the real ROI & Pricing tab quote.
 */
function ValueOpportunitySnapshot({ workspace, onNavigate, showReviewLink = true, liveInput, compact = false }: {
  workspace: PublicWorkspace | AdminWorkspace
  onNavigate: (tab: CustomerTab) => void
  showReviewLink?: boolean
  /** The pricing calculator's current inputs. On the Pricing tab the snapshot follows them instead of the saved inputs. */
  liveInput?: PricingV2Input
  /** Stacks the two columns, for the narrow column under the price summary. */
  compact?: boolean
}) {
  const snapshotClass = compact ? 'partnership-snapshot partnership-snapshot--compact' : 'partnership-snapshot'
  const v1CompatibleInputs = isPricingV2SavedInput(workspace.calculatorInputs) ? {} : workspace.calculatorInputs
  const enrollmentInput = workspace.pricingModel === 'enrollment_roi'
    ? { ...DEFAULT_ENROLLMENT_INPUT, ...v1CompatibleInputs } as EnrollmentRoiInput
    : null
  const enrollmentRoi = enrollmentInput ? calculateEnrollmentRoi(enrollmentInput) : null
  // The license value model is sized to active job seekers = licenses per year, and its ROI
  // multiple is reported against the live v2 quote's annual subscription (round 8, 2026-09-25) —
  // never the legacy v1 pricing engine's own annualSubscription — so both the license count and
  // the cost this ratio is measured against always match what the customer actually sees on the
  // ROI & Pricing tab, whether the workspace's saved calculatorInputs is still v1-shaped or
  // already migrated to v2.
  // Without live inputs (Overview), the saved inputs go through the same mode rule as the calculator.
  const licenseV2Input = workspace.pricingModel === 'license'
    ? liveInput ?? applyCalculatorRules(adaptSavedInputToPricingV2('license', workspace.calculatorInputs), { allowedModes: workspaceAllowedPricingModes(workspace) })
    : null
  const licenseV2Quote = licenseV2Input ? computeFullPricingV2Quote(licenseV2Input) : null
  const licenseInput = workspace.pricingModel === 'license'
    ? {
        ...DEFAULT_LICENSE_INPUT,
        ...v1CompatibleInputs,
        annualLicenses: licenseV2Quote?.licenseQuote.billedLicenses ?? (v1CompatibleInputs as Partial<LicensePricingInput>).annualLicenses ?? DEFAULT_LICENSE_INPUT.annualLicenses,
      } as LicensePricingInput
    : null
  const licenseRoi = licenseInput ? calculateLicensePricing(licenseInput, workspace.customerSegment) : null
  const licenseAnnualSubscription = licenseV2Quote?.headline.annualSubscription ?? licenseRoi?.annualSubscription ?? 0
  const licenseRoiMultiple = licenseRoi && licenseAnnualSubscription > 0 ? licenseRoi.annualEstimatedValue / licenseAnnualSubscription : 0

  if (enrollmentInput && enrollmentRoi) {
    return (
      <section className={snapshotClass} aria-label={`${workspace.accountName} baseline and opportunity`}>
        <div className="snapshot-baseline">
          <span className="section-label">Current baseline</span>
          <h3>The starting point for the business case</h3>
          <dl>
            <div><dt>Total enrollment</dt><dd>{number(enrollmentInput.enrolled)}</dd></div>
            <div><dt>First-year attrition</dt><dd>{enrollmentInput.attritionPercent}%</dd></div>
            <div><dt>Career advisors</dt><dd>{number(enrollmentInput.advisors)}</dd></div>
            <div><dt>Students retained with lift</dt><dd>{number(enrollmentRoi.studentsRetained)}</dd></div>
          </dl>
        </div>
        <div className="snapshot-return">
          <span>Estimated year-one opportunity</span>
          <strong>{compactMoney(enrollmentRoi.totalYearOneValue)}+</strong>
          <p>in retained tuition, avoided recruitment costs, enrollment value, and team capacity from the current model.</p>
          <small>{compactMoney(enrollmentRoi.tuitionKept)} in tuition retained from a {enrollmentInput.retentionLiftPercent}-point lift alone.</small>
          {showReviewLink && <button type="button" onClick={() => onNavigate('pricing')}>Review the full model<ArrowRight size={15} /></button>}
        </div>
      </section>
    )
  }
  if (licenseInput && licenseRoi) {
    const perLicenseRate = licenseV2Quote?.licenseQuote.tierPrice ?? licenseRoi.perLicenseRate
    const termYears = licenseV2Input?.termYears ?? licenseInput.termYears
    const modulesSelected = licenseV2Input?.selectedModules.length ?? licenseInput.selectedModules.length
    // Unlimited reads in enrolled students, like the calculator, and its yearly price is labeled "Per year" there.
    const unlimited = licenseV2Input?.mode === 'unlimited' && !licenseV2Quote?.anyStarterApplied
    return (
      <section className={snapshotClass} aria-label={`${workspace.accountName} license summary`}>
        <div className="snapshot-baseline">
          <span className="section-label">Current model</span>
          {unlimited ? (
            <>
              <h3>Every enrolled student, {termPhrase(termYears)}</h3>
              <dl>
                <div><dt>Enrolled students</dt><dd>{number(licenseV2Input!.enrollment)}</dd></div>
                <div><dt>Contract term</dt><dd>{termPhrase(termYears)}</dd></div>
                <div><dt>Modules selected</dt><dd>{number(modulesSelected)}</dd></div>
                <div><dt>Per enrolled student</dt><dd>{money(licenseV2Quote?.oneNumber.perEnrolledStudentPerYear ?? 0)}</dd></div>
              </dl>
            </>
          ) : (
            <>
              {/* The same wording as the Pricing tab's "Total you pay" line, so both tabs tell one license story. */}
              <h3>{[licenseTermPhrase(licenseRoi.billedLicenses, termYears), licensesPerYearPhrase(licenseRoi.billedLicenses, termYears)].filter(Boolean).join(', ')}</h3>
              <dl>
                <div><dt>{termYears > 1 ? `Licenses over ${termPhrase(termYears)}` : 'Licenses'}</dt><dd>{number(licenseRoi.billedLicenses * termYears)}</dd></div>
                <div><dt>Contract term</dt><dd>{termPhrase(termYears)}</dd></div>
                <div><dt>Modules selected</dt><dd>{number(modulesSelected)}</dd></div>
                <div><dt>Per-license rate</dt><dd>{money(perLicenseRate)}</dd></div>
              </dl>
            </>
          )}
        </div>
        <div className="snapshot-return">
          <span>Estimated annual value</span>
          <strong>{compactMoney(licenseRoi.annualEstimatedValue)}</strong>
          <p>in retained tuition, avoided recruitment costs, advisor capacity, and career outcomes using the assumptions in the ROI model.</p>
          {/* Both figures are yearly. The label matches the Pricing tab: "Annual subscription", or "Per year" for Unlimited. */}
          <small>{licenseRoiMultiple.toFixed(1)}x the {money(licenseAnnualSubscription)} {unlimited ? 'you pay per year' : 'annual subscription'}.</small>
          {showReviewLink && <button type="button" onClick={() => onNavigate('pricing')}>Review the full model<ArrowRight size={15} /></button>}
        </div>
      </section>
    )
  }
  return null
}

function OverviewTab({ workspace, onNavigate, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; onNavigate: (tab: CustomerTab) => void; onTrack?: (eventType: string, targetId?: string) => void }) {
  const businessRef = useRef<HTMLElement>(null)
  const nextRef = useRef<HTMLElement>(null)
  const recordingsRef = useRef<HTMLElement>(null)
  const collateralRef = useRef<HTMLElement>(null)
  const recordings = workspace.assets.filter((asset) => asset.sectionKey === 'recordings' && asset.visibility !== 'hidden')
  const collateral = workspace.assets.filter((asset) => (asset.sectionKey === 'collateral' || asset.sectionKey === 'files') && asset.visibility !== 'hidden')
  const datedTasks = sortDatedVisibleTasks(workspace.tasks)
  const enabledOnepager = workspace.precallOnepager?.enabled ? workspace.precallOnepager : null
  const onepagerMode = overviewOnepagerMode(workspace)
  // After the first customer call the one-pager sections stop rendering and the regular Overview shows.
  const onepager = onepagerMode === 'precall' ? enabledOnepager : null
  const quickLinks = [
    { label: 'Business Case', detail: `${workspace.businessCase.length} priorities`, icon: PresentationChart, ref: businessRef },
    { label: 'Next Steps', detail: `${datedTasks.length} dated actions`, icon: CalendarBlank, ref: nextRef },
    { label: 'Meeting Recordings', detail: recordings.length ? `${recordings.length} recording${recordings.length > 1 ? 's' : ''}` : 'None yet', icon: PlayCircle, ref: recordingsRef },
    ...(collateral.length ? [{ label: 'Custom Collateral', detail: `${collateral.length} resources`, icon: FolderOpen, ref: collateralRef }] : []),
  ]

  const roiSnapshot = <ValueOpportunitySnapshot workspace={workspace} onNavigate={onNavigate} />

  if (onepagerMode === 'prequalification' && enabledOnepager) {
    return <PrequalificationOnepager onepager={enabledOnepager} segment={workspace.customerSegment} />
  }

  return (
    <div className="overview-stack">
      {onepager && (
        <>
          <section className="onepager-problem">
            <h2 className="onepager-heading">The situation today</h2>
            <p>{onepager.problem}</p>
          </section>

          <section className="onepager-cost">
            <div className="onepager-cost-copy">
              <h2 className="onepager-heading">What it costs to wait</h2>
              <p>{onepager.costOfInaction}</p>
            </div>
            {roiSnapshot}
          </section>

          <section className="onepager-how-it-works">
            <h2 className="onepager-heading">How Prentus Works</h2>
            <ol className="onepager-steps">
              {onepager.steps.map((step, index) => (
                <li key={`${step.title}-${index}`}>
                  <span>{index + 1}</span>
                  <div><strong>{step.title}</strong><p>{step.detail}</p></div>
                </li>
              ))}
            </ol>
          </section>

          <section className="onepager-proof">
            <h2 className="onepager-heading">Why this works</h2>
            <ul className="onepager-proof-list">
              {onepager.proofPoints.map((point, index) => (
                <li key={index}><Check size={14} weight="bold" />{point}</li>
              ))}
            </ul>
          </section>

          <section className="onepager-next-step">
            <h2 className="onepager-heading onepager-heading-compact">Next step</h2>
            <p>{onepager.nextStep}</p>
          </section>
        </>
      )}

      <section className="overview-intro">
        <div>
          <h2>Partnership overview</h2>
          <ul className="overview-summary-list">
            {summaryBullets(workspace.recap || workspace.overview).map((bullet) => <li key={bullet}>{bullet}</li>)}
          </ul>
        </div>
        <div className="quick-links" aria-label="Overview quick links">
          {quickLinks.map((item) => {
            const Icon = item.icon
            return (
              <button type="button" key={item.label} onClick={() => item.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <Icon size={20} aria-hidden="true" />
                <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            )
          })}
        </div>
      </section>

      {!onepager && roiSnapshot}

      <CustomerLogoBar segment={workspace.customerSegment} />

      <section className="transformation-section">
        <div className="transformation-copy">
          <span className="section-label">The Prentus 4P framework</span>
          <h2>A partner for the transformation, not another point solution</h2>
          <p>Prentus brings the system, rollout process, people, and proof {workspace.accountName} needs to move career outcomes from a set of services into something the institution can run and prove.</p>
          <ol>
            <li><b>1</b><div><strong>Platform</strong><span>One connected system from day one to a verified hire.</span></div></li>
            <li><b>2</b><div><strong>Process</strong><span>Find where career integration can happen, wire it into the workflow, launch with the team, and keep improving.</span></div></li>
            <li><b>3</b><div><strong>People</strong><span>Founder-led implementation, advisor enablement, and hands-on adoption support.</span></div></li>
            <li><b>4</b><div><strong>Proof</strong><span>Engagement, hires, and leadership reporting {workspace.accountName} can defend.</span></div></li>
          </ol>
        </div>
        <figure className="transformation-product">
          <img src="/images/product/platform-overview.webp" alt="Prentus student and advisor platform screens" />
          <figcaption>One system for {houseNoun(workspace.customerSegment).plural}, advisors, employers, and leadership.</figcaption>
        </figure>
      </section>

      {workspace.businessCase.length > 0 && <section className="overview-section business-case" ref={businessRef}>
        <SectionHeader title="Business Case" description={`Why this partnership matters for your ${houseNoun(workspace.customerSegment).plural} and your team.`} />
        <ol>
          {workspace.businessCase.map((point, index) => <li key={point.id}><span>{index + 1}</span><p className="value-first-point">{point.body}</p></li>)}
        </ol>
      </section>}

      {datedTasks.length > 0 && <section className="overview-section" ref={nextRef}>
        <SectionHeader title="Next Steps" description="The shared path from evaluation to launch." />
        <div className="task-list">
          {datedTasks.map((task) => (
            <article className="task-row" key={task.id}>
              <span className={`task-status ${task.status}`} aria-label={task.status}>{task.status === 'complete' ? <Check size={14} weight="bold" /> : <CalendarBlank size={13} />}</span>
              <div><strong>{task.title}</strong>{task.description && <p>{task.description}</p>}</div>
              <span className="task-owner">{task.ownerName}</span>
              <time dateTime={task.dueDate || undefined}><CalendarBlank size={15} />{shortDate(task.dueDate)}</time>
            </article>
          ))}
        </div>
      </section>}

      <section className="overview-section">
        <SectionHeader title="People Involved" description="The people helping evaluate and move this work forward." />
        <div className="people-grid">
          {workspace.members.map((member) => (
            <article className="person-row" key={member.id}>
              {member.avatarUrl
                ? <img className="avatar avatar-photo" src={member.avatarUrl} alt={member.name} />
                : <span className={member.side === 'prentus' ? 'avatar avatar-prentus' : 'avatar'}>{initials(member.name)}</span>}
              <div><strong>{member.name}</strong><p>{member.title || (member.side === 'prentus' ? 'Prentus team' : workspace.accountName)}</p></div>
              {member.bookingUrl && <a href={member.bookingUrl} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', member.id)}>Book time<ArrowSquareOut size={14} /></a>}
            </article>
          ))}
        </div>
      </section>

      <section className="overview-section" ref={recordingsRef}>
        <SectionHeader title="Meeting Recordings" description="Conversations your team can revisit and share." />
        <RecordingCards assets={recordings} onTrack={onTrack} />
      </section>

      {collateral.length > 0 && (
        <section className="overview-section" ref={collateralRef}>
          <SectionHeader title="Custom Collateral" description={`Materials created specifically for ${workspace.accountName}.`} />
          <CollateralGrid assets={collateral} accountName={workspace.accountName} onTrack={onTrack} />
        </section>
      )}

      <section className="overview-cta">
        <div><SquaresFour size={22} weight="duotone" /><span><strong>Want to see the platform in more detail?</strong><small>Review the capabilities selected for your team.</small></span></div>
        <button type="button" className="button button-primary" onClick={() => onNavigate('product')}>Explore Product</button>
      </section>
    </div>
  )
}

/** How many shared documents the Customer Success Overview lists before it links to Projects. */
const SHARED_WITH_YOU_LIMIT = 6

function SuccessOverviewTab({ workspace, onNavigate, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; onNavigate: (tab: CustomerTab) => void; onTrack?: (eventType: string, targetId?: string) => void }) {
  const summary = workspace.successContent?.summary || null
  const metrics = workspace.successContent?.metrics || []
  const prentusTeam = workspace.members.filter((member) => member.side === 'prentus')
  const customerTeam = workspace.members.filter((member) => member.side === 'customer')
  const recapBullets = summaryBullets(workspace.recap || workspace.overview)
  // The calculator leads the Overview whenever the customer can use it (Rod, 2026-10-29).
  // A locked calculator gets no card: the pricing tab already explains the lock.
  const pricingOpen = Boolean(workspace.pricingModel && !workspace.pricingLocked)
  const projects = customerVisibleProjects(workspace.projects)
  const sharedDocuments = sharedWithYouDocuments(workspace)
  const sharedLabels = Object.fromEntries(sharedDocuments.map(({ asset, projectName }) => [asset.id, projectName]))

  return (
    <div className="overview-stack">
      {pricingOpen && (
        <section className="overview-cta" aria-label="Renewal pricing">
          <div><PresentationChart size={22} weight="duotone" aria-hidden="true" /><span><strong>Explore Your Renewal Pricing</strong><small>Set your licenses and add-ons to see your price and the value for your team.</small></span></div>
          <Button type="button" onClick={() => onNavigate('pricing')}>Open ROI & Pricing</Button>
        </section>
      )}

      {recapBullets.length > 0 && (
        <section className="overview-intro success-intro">
          <div>
            <h2>Partnership overview</h2>
            <ul className="overview-summary-list">
              {recapBullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
            </ul>
          </div>
        </section>
      )}

      {summary && (
        <section className="overview-section success-summary">
          <SectionHeader title="Executive Summary" description="The current terms of your Prentus partnership." />
          <dl className="success-summary-grid">
            {(summary.contractStart || summary.contractEnd) && <div><dt>Contract effective dates</dt><dd>{[summary.contractStart ? contractDate(summary.contractStart) : null, summary.contractEnd ? contractDate(summary.contractEnd) : null].filter(Boolean).join(' – ')}</dd></div>}
            {summary.customUrl && <div><dt>Custom URL</dt><dd><a href={`https://${summary.customUrl.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', 'success-custom-url')}>{summary.customUrl.replace(/^https?:\/\//, '')}<ArrowSquareOut size={14} /></a></dd></div>}
            {summary.licenses !== null && <div><dt>Active jobseeker licenses</dt><dd>{number(summary.licenses)}</dd></div>}
          </dl>
          {summary.addOns.length > 0 && (
            <div className="success-addons">
              <span>Add-ons</span>
              <div>{summary.addOns.map((addOn) => <b key={addOn}><Check size={13} weight="bold" />{addOn}</b>)}</div>
            </div>
          )}
        </section>
      )}

      {metrics.length > 0 && (
        <section className="overview-section success-metrics">
          <SectionHeader title="Success Metrics" description="The goals we set together and where they stand." />
          <div className="success-metric-grid">
            {metrics.map((metric) => (
              <article key={metric.id} className="success-metric-card">
                <span className="metric-label">{metric.label}</span>
                <strong>{metric.current || '–'}</strong>
                <small>Target: {metric.target || '–'}</small>
                {metric.status && <b className={`metric-status ${metric.status}`}>{metric.status === 'on_track' ? 'On track' : metric.status === 'at_risk' ? 'At risk' : 'Achieved'}</b>}
              </article>
            ))}
          </div>
        </section>
      )}

      {projects.length > 0 && (
        <section className="overview-section">
          <SectionHeader title="What We're Working On" description="Where each project stands and what happens next." />
          <div className="success-project-list">
            {projects.map((project) => {
              const done = project.tasks.filter((task) => task.status === 'complete').length
              const next = nextOpenProjectTask(project.tasks)
              return (
                <article className="success-project-row" key={project.id}>
                  <div className="success-project-head">
                    <strong>{project.name}</strong>
                    <b className={`project-status ${project.status}`}>{PROJECT_STATUS_LABEL[project.status]}</b>
                  </div>
                  {project.tasks.length > 0 && (
                    <div className="success-project-progress">
                      <span>{`${done} of ${project.tasks.length} ${project.tasks.length === 1 ? 'step' : 'steps'} complete`}</span>
                      <i aria-hidden="true"><b style={{ width: `${Math.round((done / project.tasks.length) * 100)}%` }} /></i>
                    </div>
                  )}
                  {next && (
                    <p className="success-project-next">
                      <span>Next step</span>
                      <strong>{next.title}</strong>
                      {(next.ownerName || next.dueDate) && <small>{[next.ownerName, next.dueDate ? `Due ${shortDate(next.dueDate)}` : null].filter(Boolean).join(' · ')}</small>}
                    </p>
                  )}
                </article>
              )
            })}
          </div>
          <div className="overview-section-action">
            <Button type="button" variant="secondary" compact onClick={() => onNavigate('projects')}>Open Projects</Button>
          </div>
        </section>
      )}

      {sharedDocuments.length > 0 && (
        <section className="overview-section">
          <SectionHeader title="Shared With You" description="Briefs, prototypes and documents we made for your team." />
          <CollateralGrid assets={sharedDocuments.slice(0, SHARED_WITH_YOU_LIMIT).map(({ asset }) => asset)} labels={sharedLabels} accountName={workspace.accountName} onTrack={onTrack} />
          {sharedDocuments.length > SHARED_WITH_YOU_LIMIT && (
            <div className="overview-section-action">
              <Button type="button" variant="secondary" compact onClick={() => onNavigate('projects')}>See All in Projects</Button>
            </div>
          )}
        </section>
      )}

      <section className="overview-section">
        <SectionHeader title="Your Prentus Team" description="Reach out any time. We read everything." />
        <div className="people-grid">
          {prentusTeam.map((member) => (
            <article className={member.bookingUrl || member.email ? 'person-row' : 'person-row no-action'} key={member.id}>
              {member.avatarUrl
                ? <img className="avatar avatar-photo" src={member.avatarUrl} alt={member.name} />
                : <span className="avatar avatar-prentus">{initials(member.name)}</span>}
              <div><strong>{member.name}</strong><p>{member.title || 'Prentus team'}</p></div>
              {member.bookingUrl
                ? <a href={member.bookingUrl} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', member.id)}>Book time<ArrowSquareOut size={14} /></a>
                : member.email && <a href={`mailto:${member.email}`}>Email</a>}
            </article>
          ))}
        </div>
      </section>

      {customerTeam.length > 0 && (
        <section className="overview-section">
          <SectionHeader title={`The ${workspace.accountName} Team`} description="The people on your side of the partnership." />
          <div className="people-grid">
            {customerTeam.map((member) => (
              <article className="person-row no-action" key={member.id}>
                {member.avatarUrl
                  ? <img className="avatar avatar-photo" src={member.avatarUrl} alt={member.name} />
                  : <span className="avatar">{initials(member.name)}</span>}
                <div><strong>{member.name}</strong><p>{member.title || workspace.accountName}</p></div>
              </article>
            ))}
          </div>
        </section>
      )}

    </div>
  )
}

function CallsTab({ workspace, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; onTrack?: (eventType: string, targetId?: string) => void }) {
  const recordings = callRecordings(workspace.assets)
  if (!recordings.length) {
    return (
      <section className="standard-tab">
        <SectionHeader title="Calls" description="Every call between your team and Prentus, newest first." />
        <div className="empty-state"><Clock size={22} /><p>Calls show up here after each conversation.</p></div>
      </section>
    )
  }
  return (
    <section className="standard-tab">
      <SectionHeader title="Calls" description="Every call between your team and Prentus, newest first." />
      <div className="call-library">
        {recordings.map((asset) => {
          const time = recordingTime(asset)
          return (
            <article className="call-row" key={asset.id}>
              <span className="call-date"><CalendarBlank size={16} />{time ? new Date(time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Date to confirm'}</span>
              <div className="call-copy">
                <strong>{asset.title}</strong>
                {asset.description && <p>{asset.description}</p>}
              </div>
              {asset.url
                ? <a className="button button-secondary compact" href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}><PlayCircle size={16} />Watch recording</a>
                : <span className="coming-soon">Recording not available yet</span>}
            </article>
          )
        })}
      </div>
    </section>
  )
}

function contractDate(value: string): string {
  const parsed = Date.parse(`${value}T12:00:00`)
  return Number.isNaN(parsed) ? value : new Date(parsed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function RenewalTab({ workspace, renewal, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; renewal: NonNullable<PublicWorkspace['renewal']>; onTrack?: (eventType: string, targetId?: string) => void }) {
  const documents = workspace.assets.filter((asset) => asset.sectionKey === 'renewal' && asset.visibility !== 'hidden')
  const termRange = (terms: NonNullable<typeof renewal.current>) =>
    [terms.startDate ? contractDate(terms.startDate) : null, terms.endDate ? contractDate(terms.endDate) : null].filter(Boolean).join(' – ')
  return (
    <section className="standard-tab">
      <SectionHeader title={renewal.headline || (workspace.phase === 'customer_success' ? 'Renewal' : 'Proposal')} description={renewal.current ? `The terms on the table for ${workspace.accountName}, with your current agreement for reference.` : `The options on the table for ${workspace.accountName}.`} />
      {renewal.summary && <p className="renewal-summary">{renewal.summary}</p>}

      {renewal.current && (
        <section className="renewal-current" aria-label="Current agreement">
          <span className="project-plan-label">{renewal.current.termLabel || 'Current agreement'}{termRange(renewal.current) ? ` · ${termRange(renewal.current)}` : ''}</span>
          <dl>
            {renewal.current.licenses !== null && <div><dt>Licenses</dt><dd>{number(renewal.current.licenses)}</dd></div>}
            {renewal.current.perLicenseCost !== null && <div><dt>Per license</dt><dd>{unitMoney(renewal.current.perLicenseCost)}</dd></div>}
            {renewal.current.totalCost !== null && <div><dt>Total</dt><dd>{money(renewal.current.totalCost)}</dd></div>}
            {renewal.current.billingFrequency && <div><dt>Billing</dt><dd>{renewal.current.billingFrequency}</dd></div>}
          </dl>
        </section>
      )}

      {renewal.options.length > 0 && (
        <div className={renewal.options.length > 1 ? 'renewal-options multiple' : 'renewal-options'}>
          {renewal.options.map((option) => <RenewalOptionCard key={option.id} option={option} />)}
        </div>
      )}

      {renewal.highlights.length > 0 && (
        <section className="renewal-highlights">
          <h3>What changes</h3>
          <ul>{renewal.highlights.map((item) => <li key={item}><Check size={14} weight="bold" />{item}</li>)}</ul>
        </section>
      )}

      {documents.length > 0 && (
        <section className="renewal-documents">
          <h3>Documents</h3>
          <CollateralGrid assets={documents} accountName={workspace.accountName} onTrack={onTrack} />
        </section>
      )}
    </section>
  )
}

function OrderFormSummary({ form, accountName, showDetails = false }: { form: OrderForm; accountName: string; /** Also lists the details the customer can correct. */ showDetails?: boolean }) {
  const term = form.termStart && form.termEnd ? `${contractDate(form.termStart)} to ${contractDate(form.termEnd)}` : form.termStart ? `Starts ${contractDate(form.termStart)}` : 'To be confirmed'
  const address = form.customer.address.trim().replace(/\s*\n\s*/g, ', ')
  const billing = billingContactLine(form.customer) || [form.customer.contactName, form.customer.contactEmail].filter(Boolean).join(', ')
  const po = (form.customer.poNumber || '').trim()
  return (
    <dl className="order-form-summary">
      <div><dt>Account</dt><dd>{accountName}</dd></div>
      <div><dt>Order Form</dt><dd>{form.number}</dd></div>
      <div><dt>Term</dt><dd>{term}</dd></div>
      <div><dt>Total</dt><dd>{money(orderFormTotals(form).total)}</dd></div>
      {showDetails && <div className="order-form-summary-wide"><dt>Company Address</dt><dd>{address || 'Not set'}</dd></div>}
      {showDetails && <div className="order-form-summary-wide"><dt>Billing Contact</dt><dd>{billing || 'Not set'}</dd></div>}
      {showDetails && po && <div className="order-form-summary-wide"><dt>PO Number</dt><dd>{po}</dd></div>}
    </dl>
  )
}

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/**
 * The customer corrects their own details on a form out for signature:
 * company address, billing contact and PO number. Pricing and terms are not
 * here and the server refuses them. Saving replaces the signing form.
 */
function EditDetailsDialog({ form, open, onClose, onSave, waitingOnPrentus }: { form: OrderForm; open: boolean; onClose: () => void; onSave: (details: CustomerDetails) => Promise<{ changed: boolean }>; waitingOnPrentus: boolean }) {
  const toast = useToast()
  const [draft, setDraft] = useState<CustomerDetails>(() => customerDetails(form))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  useEffect(() => { if (open) { setDraft(customerDetails(form)); setError(''); setEmailError('') } }, [open, form])
  const formId = `order-form-details-${form.id}`
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next: CustomerDetails = { address: draft.address.trim(), billingName: draft.billingName.trim(), billingEmail: draft.billingEmail.trim().toLowerCase(), poNumber: draft.poNumber.trim() }
    if (next.billingEmail && !EMAIL_PATTERN.test(next.billingEmail)) { setEmailError('Enter a valid email, or leave it blank.'); return }
    setEmailError('')
    setError('')
    setSaving(true)
    try {
      const result = await onSave(next)
      onClose()
      if (result.changed) toast.success('Details Updated', { description: waitingOnPrentus ? 'Prentus has your changes. The order form is ready to sign once Prentus signs again.' : 'The signing form below now shows your changes.' })
      else toast.info('Nothing Changed')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Your changes were not saved. Try again in a minute.')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      dismissible={!saving}
      busy={saving}
      title="Edit Your Details"
      description="Correct your company address, billing contact or PO number. Pricing and terms stay as Prentus prepared them. Saving updates the order form, and Prentus gets a note of the change."
      footer={<>
        <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>Cancel</Button>
        <Button type="submit" form={formId} disabled={saving}>{saving ? <SpinnerGap size={16} className="spin" /> : null}Save Changes</Button>
      </>}
    >
      <form id={formId} className="order-form-details-form" onSubmit={(event) => void submit(event)} noValidate>
        {error && <Alert tone="danger" description={error} />}
        <Field label="Company Address">
          <textarea rows={3} maxLength={600} value={draft.address} onChange={(event) => setDraft({ ...draft, address: event.target.value })} autoComplete="street-address" />
        </Field>
        <Field label="Billing Contact Name" hint="Leave blank if the business contact handles billing.">
          <input className="ui-input" maxLength={200} value={draft.billingName} onChange={(event) => setDraft({ ...draft, billingName: event.target.value })} autoComplete="name" />
        </Field>
        <Field label="Billing Contact Email" error={emailError || undefined}>
          <input className="ui-input" type="email" maxLength={254} value={draft.billingEmail} onChange={(event) => setDraft({ ...draft, billingEmail: event.target.value })} autoComplete="email" />
        </Field>
        <Field label="PO Number" hint="Optional. Add it if your invoices need one.">
          <input className="ui-input" maxLength={60} value={draft.poNumber} onChange={(event) => setDraft({ ...draft, poNumber: event.target.value })} />
        </Field>
      </form>
    </Dialog>
  )
}

/**
 * The customer's Order Form tab. Prentus signs first, so a form out for
 * signature is "Being Prepared" until then; after that the customer signer
 * sees a short summary and the signing form, open. Colleagues see whose turn
 * it is. The admin preview stays read-only: no signing, no polling.
 */
export function OrderFormTab({ workspace, adminMode, liveDocuments, onTrack, onActivity, onRefresh, onSaveDetails, initialSignedFormId = null }: { workspace: PublicWorkspace | AdminWorkspace; adminMode: boolean; liveDocuments: boolean; onTrack?: (eventType: string, targetId?: string) => void; /** Customer view only: order form activity for Prentus (views and the start of signing). */ onActivity?: OrderFormActivityReporter; onRefresh?: () => Promise<void>; /** Saves the customer's own details (address, billing contact, PO number) on a form out for signature. Customer view only. */ onSaveDetails?: (formId: string, details: CustomerDetails) => Promise<{ changed: boolean }>; /** Starts in the post-signing state for this form. Only for local review pages; production never sets it. */ initialSignedFormId?: string | null }) {
  const forms = workspace.orderForms.filter(customerVisibleOrderForm)
  const [selectedId, setSelectedId] = useState(forms[0]?.id || '')
  const selected = forms.find((form) => form.id === selectedId) || forms[0]
  // The form the viewer just signed in the embed; the webhook catches up a few seconds later.
  const [justSigned, setJustSigned] = useState<string | null>(initialSignedFormId)
  const [waitExpired, setWaitExpired] = useState(false)
  const [editing, setEditing] = useState(false)
  const refreshRef = useRef(onRefresh)
  refreshRef.current = onRefresh
  const { signedCopy } = selected ? signedDocuments(selected, workspace.assets) : { signedCopy: null }
  const settled = Boolean(selected && selected.status === 'signed' && (signedCopy?.url || !selected.shareSignedCopy))
  // Prentus hears when the customer opens a form that is out for signature.
  // The reporter keeps it to once per form per browser session.
  const viewedFormId = !adminMode && selected?.status === 'sent' ? selected.id : null
  const activityRef = useRef(onActivity)
  activityRef.current = onActivity
  useEffect(() => {
    if (viewedFormId) activityRef.current?.(viewedFormId, 'viewed')
  }, [viewedFormId])
  useEffect(() => {
    if (!justSigned || settled) return
    let tries = 0
    const timer = window.setInterval(() => {
      tries += 1
      if (tries > 20) { window.clearInterval(timer); setWaitExpired(true); return }
      void refreshRef.current?.().catch(() => undefined)
    }, 3000)
    return () => window.clearInterval(timer)
  }, [justSigned, settled])
  if (!selected) return null
  const printHref = adminMode ? `/admin/workspaces/${workspace.id}/order-forms/${selected.id}` : `/${workspace.publicId}/order-forms/${selected.id}`
  const pdfHref = adminMode ? `/api/admin/workspaces/${workspace.id}/order-forms/${selected.id}/pdf` : `/api/workspaces/${workspace.publicId}/order-forms/${selected.id}/pdf`
  const stage = orderFormStage(selected)
  const signingUrl = adminMode ? null : viewerSigningUrl(selected)
  const customerSigner = selected.signing?.signers.find((signer) => signer.role === 'Customer') || null
  const signerFirstName = customerSigner?.name.split(' ')[0] || orderFormCustomerSigner(selected.customer).name.split(' ')[0] || 'your signer'
  const contact = selected.preparedBy?.email ? selected.preparedBy : selected.prentusContact
  const contactLine = contact.email ? <>Questions? Contact {contact.name || 'your Prentus contact'} at <a href={`mailto:${contact.email}`}>{contact.email}</a>.</> : <>Questions? Contact your Prentus representative.</>
  const viewPdf = <a className="button button-secondary compact" href={pdfHref} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_open', selected.id)}><Eye size={15} />View PDF</a>
  const staged = !adminMode && Boolean(selected.signing) && selected.status === 'sent'
  // Any verified member of the customer may correct the address, billing
  // contact or PO number until the customer signer has signed.
  const canEditDetails = staged && Boolean(onSaveDetails) && customerEditBlocked(selected) === null
  const afterSigning = !adminMode && justSigned === selected.id
  const viewerEmail = 'viewerEmail' in workspace ? workspace.viewerEmail : ''
  const viewerSigns = Boolean(customerSigner && viewerEmail && customerSigner.email.trim().toLowerCase() === viewerEmail.trim().toLowerCase())
  const description = selected.status === 'signed'
    ? 'Your signed agreement with Prentus.'
    : staged
      ? 'Your order form with Prentus and its signing status.'
      : 'Review the order form below. Download it as a PDF or reach out to your Prentus contact with questions before signing.'

  // The packet, or the signed copy once there is one. Fixtures render the HTML version.
  const agreement = signedCopy?.url
    ? <div className="order-form-viewer"><iframe src={`${signedCopy.url}#toolbar=1&view=FitH`} title={`Signed order form ${selected.number}`} /></div>
    : liveDocuments
      ? <div className="order-form-viewer"><iframe src={`${pdfHref}#toolbar=1&view=FitH`} title={`Order form ${selected.number}`} /></div>
      : <div className="order-form-viewer html"><OrderFormDocument form={selected} accountName={workspace.accountName} accountLogoUrl={workspace.accountLogoUrl} assets={workspace.assets} /></div>

  function completed() {
    onTrack?.('order_form_signed', selected!.id)
    setWaitExpired(false)
    setJustSigned(selected!.id)
  }

  return (
    <section className="standard-tab">
      <SectionHeader title="Order Form" description={description} />
      {forms.length > 1 && (
        <div className="order-form-switcher" role="tablist" aria-label="Order forms">
          {forms.map((form) => (
            <button type="button" key={form.id} role="tab" aria-selected={form.id === selected.id} className={form.id === selected.id ? 'active' : ''} onClick={() => setSelectedId(form.id)}>
              <strong>{form.title || form.number}</strong><small>{ORDER_FORM_CUSTOMER_STAGE_LABEL[orderFormStage(form)]} · {money(orderFormTotals(form).total)}</small>
            </button>
          ))}
        </div>
      )}

      {afterSigning && (
        <div className="order-form-tab-state">
          <Alert
            tone="success"
            title="You're All Set"
            description={<>Thank you{customerSigner?.name ? `, ${customerSigner.name.split(' ')[0]}` : ''}. Your signature is in, and the order form is signed by both parties. What happens next: you get the signed copy by email, and {contact.name ? contact.name.split(' ')[0] : 'your Prentus contact'} follows up with next steps. {contactLine}</>}
            action={settled && signedCopy?.url
              ? <a className="button button-primary compact" href={downloadUrl(signedCopy.url)} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_signed_download', selected.id)}><File size={15} />Download Signed Copy</a>
              : undefined}
          />
          {!settled && <p className="order-form-quiet">{waitExpired ? 'Your signed copy is on its way by email. Reload this page in a minute to download it here.' : <><SpinnerGap size={14} className="spin" /> Preparing your signed copy…</>}</p>}
          {agreement}
        </div>
      )}

      {staged && !afterSigning && (
        <div className="order-form-tab-state">
          {stage === 'awaiting_prentus' && customerSigner?.status !== 'completed' && (
            <Alert tone="info" title="Being Prepared" description={<>Prentus is finalizing this order form. {viewerSigns ? 'You get an email' : `${signerFirstName} gets an email`} as soon as it is ready to sign. {contactLine}</>} />
          )}
          {stage === 'awaiting_prentus' && customerSigner?.status === 'completed' && (
            <Alert tone="success" title={`Signed by ${viewerSigns ? 'You' : signerFirstName}`} description={<>Prentus signs next, and the signed copy appears here. {contactLine}</>} />
          )}
          {stage === 'awaiting_customer' && !signingUrl && (
            <Alert tone="info" title={`Waiting on ${signerFirstName} to Sign`} description={<>{customerSigner?.name || 'Your signer'} has the signing request. The signed copy appears here once they sign. {contactLine}</>} />
          )}
          {stage === 'declined' && (
            <Alert tone="danger" title="Declined" description={<>This order form was declined and is no longer open for signature. To talk through changes, contact {contact.name || 'your Prentus contact'}{contact.email ? <> at <a href={`mailto:${contact.email}`}>{contact.email}</a></> : null}.</>} />
          )}
          <div className="order-form-tab-status">
            <StatusBadge tone={ORDER_FORM_STAGE_BADGE[stage]}>{ORDER_FORM_CUSTOMER_STAGE_LABEL[stage]}</StatusBadge>
            {stage !== 'awaiting_prentus' || customerSigner?.status === 'completed' ? viewPdf : null}
            {canEditDetails && <button type="button" className="button button-secondary compact" onClick={() => setEditing(true)}><PencilSimple size={15} />Edit Your Details</button>}
          </div>
          <OrderFormSummary form={selected} accountName={workspace.accountName} showDetails={canEditDetails} />
          {canEditDetails && onSaveDetails && <EditDetailsDialog form={selected} open={editing} onClose={() => setEditing(false)} waitingOnPrentus={stage === 'awaiting_prentus'} onSave={(details) => onSaveDetails(selected.id, details)} />}
          {signingUrl && stage === 'awaiting_customer' && (
            <>
              <div className="order-form-sign-callout">
                <div><strong>Ready for your signature</strong><span>Prentus has signed. Review and sign below. View PDF opens the full order form and the Cloud Service Agreement.</span></div>
              </div>
              <div className="order-form-signing-embed" onPointerDownCapture={() => onActivity?.(selected.id, 'signing_started')} onKeyDownCapture={() => onActivity?.(selected.id, 'signing_started')}>
                <DocusealForm key={signingUrl} src={signingUrl} withTitle={false} onComplete={completed} />
              </div>
            </>
          )}
          {/* The agreement inline, except while Being Prepared and on Your Turn (the signing form shows it). */}
          {!signingUrl && !(stage === 'awaiting_prentus' && customerSigner?.status !== 'completed') && agreement}
        </div>
      )}

      {!staged && !afterSigning && (<>
        {!adminMode && liveDocuments && selected.status === 'signed' && <YourInvoice publicId={workspace.publicId} formId={selected.id} onTrack={onTrack} />}
        {signedCopy?.url && (
          <div className="order-form-sign-callout signed">
            <div><strong>Signed by both parties</strong><span>This is the completed contract, with the agreement.</span></div>
            <a className="button button-primary compact" href={downloadUrl(signedCopy.url)} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_signed_download', selected.id)}><File size={15} />Download Signed Copy</a>
          </div>
        )}
        {signedCopy?.url && (
          <div className="order-form-signed-embed">
            <iframe src={`${signedCopy.url}#toolbar=1&view=FitH`} title={`Signed order form ${selected.number}`} />
            <p>If the document does not display here, use Download Signed Copy above.</p>
          </div>
        )}
        <div className="order-form-actions">
          <StatusBadge tone={ORDER_FORM_STAGE_BADGE[stage]}>{(adminMode ? ORDER_FORM_STAGE_LABEL : ORDER_FORM_CUSTOMER_STAGE_LABEL)[stage]}</StatusBadge>
          <span className="order-form-action-links">
            <a className="button button-secondary compact" href={printHref} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_open', selected.id)}><Eye size={15} />Full Page</a>
            <a className="button button-primary compact" href={`${pdfHref}?download=1`} onClick={() => onTrack?.('order_form_pdf', selected.id)}><File size={15} />Download PDF</a>
          </span>
        </div>
        {!signedCopy?.url && (liveDocuments
          ? <div className="order-form-signed-embed"><iframe src={`${pdfHref}#toolbar=1&view=FitH`} title={`Order form ${selected.number}`} /><p>The order form and the Cloud Service Agreement, as one document. If it does not display here, use Download PDF above.</p></div>
          : <OrderFormDocument form={selected} accountName={workspace.accountName} accountLogoUrl={workspace.accountLogoUrl} assets={workspace.assets} />)}
      </>)}
    </section>
  )
}

/**
 * Your Invoice: once Stripe issues the invoice for a signed form, links to
 * the hosted invoice page and the PDF, so the signer can forward it to
 * accounts payable. Shows nothing until the invoice exists.
 */
function YourInvoice({ publicId, formId, onTrack }: { publicId: string; formId: string; onTrack?: (eventType: string, targetId?: string) => void }) {
  const [invoice, setInvoice] = useState<OrderFormInvoiceLinks | null>(null)
  useEffect(() => {
    let live = true
    setInvoice(null)
    api.loadOrderFormInvoice(publicId, formId).then((result) => { if (live) setInvoice(result.invoice) }).catch(() => undefined)
    return () => { live = false }
  }, [publicId, formId])
  if (!invoice) return null
  const paid = invoice.status === 'paid'
  const detail = [invoice.number ? `Invoice ${invoice.number}` : 'Invoice', invoice.amountDue !== null && !paid ? `${money(invoice.amountDue)} due` : null, paid ? 'Paid' : invoice.dueDate ? `Due ${formatDisplayDate(invoice.dueDate.slice(0, 10))}` : null].filter(Boolean).join(' · ')
  return (
    <div className="order-form-invoice">
      <div>
        <strong>Your Invoice</strong>
        <span>{detail}. Pay by ACH or bank transfer, or forward it to your accounts payable team.</span>
      </div>
      <div className="order-form-invoice-actions">
        <a className="button button-primary compact" href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_invoice_open', formId)}>View Invoice<ArrowSquareOut size={15} /></a>
        {invoice.invoicePdf && <a className="button button-secondary compact" href={invoice.invoicePdf} target="_blank" rel="noreferrer" onClick={() => onTrack?.('order_form_invoice_pdf', formId)}><File size={15} />Invoice PDF</a>}
      </div>
    </div>
  )
}

function RenewalOptionCard({ option }: { option: RenewalOption }) {
  const { subtotal, discount, total } = optionTotals(option)
  const term = [option.termStart ? contractDate(option.termStart) : null, option.termEnd ? contractDate(option.termEnd) : null].filter(Boolean).join(' – ')
  const rows: Array<{ key: string; name: string; description: string; quantity: number | null; unitPrice: number | null; unitLabel: string | null }> = [
    ...(option.licenseQuantity !== null || option.licenseUnitPrice !== null
      ? [{ key: 'licenses', name: option.licenseLabel || 'Active jobseeker licenses', description: '', quantity: option.licenseQuantity, unitPrice: option.licenseUnitPrice, unitLabel: null }]
      : []),
    ...option.addOns.map((addOn) => ({ key: addOn.id, name: addOn.name, description: addOn.description, quantity: addOn.quantity, unitPrice: addOn.unitPrice, unitLabel: addOn.unitLabel })),
  ]
  return (
    <article className={option.recommended ? 'renewal-option recommended' : 'renewal-option'}>
      <header>
        <div>
          <h3>{option.name || 'Renewal option'}</h3>
          {term && <small>{term}{option.billingFrequency ? ` · ${option.billingFrequency}` : ''}</small>}
        </div>
        {option.recommended && <b>Recommended</b>}
      </header>
      {option.description && <p className="renewal-option-note">{option.description}</p>}
      <div className="renewal-option-lines">
        {rows.map((row) => (
          <div className="renewal-option-line" key={row.key}>
            <div>
              <strong>{row.name}</strong>
              {row.description && <small>{row.description}</small>}
              {(row.quantity !== null || row.unitPrice !== null) && (
                <em>
                  {row.quantity !== null ? number(row.quantity) : '–'}
                  {row.unitPrice !== null ? ` × ${unitMoney(row.unitPrice)}${row.unitLabel || ''}` : ''}
                </em>
              )}
            </div>
            <span>{lineTotal(row) === null ? '–' : money(lineTotal(row)!)}</span>
          </div>
        ))}
      </div>
      <footer>
        {discount > 0 && (
          <>
            <div><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="renewal-option-discount"><span>{option.discountLabel || 'Discount'}</span><span>-{money(discount)}</span></div>
          </>
        )}
        <div className="renewal-option-total"><span>Total</span><strong>{money(total)}</strong></div>
      </footer>
    </article>
  )
}

const PROJECT_STATUS_LABEL: Record<string, string> = { exploring: 'Exploring', in_progress: 'In progress', delivered: 'Delivered' }

function ProjectsTab({ workspace, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; onTrack?: (eventType: string, targetId?: string) => void }) {
  const projects = customerVisibleProjects(workspace.projects)
  return (
    <section className="standard-tab">
      <SectionHeader title="Projects" description="What we're working on together, with the plan and documents for each." />
      <div className="project-stack">
        {projects.map((project) => {
          const documents = projectDocuments(workspace.assets, project.id)
          const done = project.tasks.filter((task) => task.status === 'complete').length
          return (
            <article className="project-card" key={project.id}>
              <header className="project-head">
                <div>
                  <h3>{project.name}</h3>
                  {project.description && <p>{project.description}</p>}
                </div>
                <b className={`project-status ${project.status}`}>{PROJECT_STATUS_LABEL[project.status]}</b>
              </header>
              {project.tasks.length > 0 && (
                <div className="project-plan">
                  <span className="project-plan-label">Action plan · {done}/{project.tasks.length} complete</span>
                  <div className="task-list">
                    {project.tasks.map((task) => (
                      <article className="task-row" key={task.id}>
                        <span className={`task-status ${task.status}`} aria-label={task.status}>{task.status === 'complete' ? <Check size={14} weight="bold" /> : <CalendarBlank size={13} />}</span>
                        <div><strong>{task.title}</strong></div>
                        <span className="task-owner">{task.ownerName}</span>
                        <time dateTime={task.dueDate || undefined}><CalendarBlank size={15} />{shortDate(task.dueDate)}</time>
                      </article>
                    ))}
                  </div>
                </div>
              )}
              {documents.length > 0 && (
                <div className="project-documents">
                  <span className="project-plan-label">Documents</span>
                  <CollateralGrid assets={documents} accountName={workspace.accountName} onTrack={onTrack} />
                </div>
              )}
            </article>
          )
        })}
      </div>
    </section>
  )
}

function recordingTime(asset: WorkspaceAsset): number {
  const occurred = typeof asset.metadata?.occurredAt === 'string' ? Date.parse(asset.metadata.occurredAt) : NaN
  if (!Number.isNaN(occurred)) return occurred
  const fromDescription = Date.parse(String(asset.description || '').replace(/^Recorded\s+/i, ''))
  return Number.isNaN(fromDescription) ? 0 : fromDescription
}

function PrequalificationOnepager({ onepager, segment }: { onepager: NonNullable<PublicWorkspace['precallOnepager']>; segment: PublicWorkspace['customerSegment'] }) {
  return (
    <div className="overview-stack prequalification-onepager">
      {(onepager.audienceName || onepager.audienceTitle) && (
        <p className="onepager-audience">Prepared for <strong>{[onepager.audienceName, onepager.audienceTitle].filter(Boolean).join(', ')}</strong></p>
      )}
      <section className="onepager-problem">
        <h2 className="onepager-heading">The situation today</h2>
        <p>{onepager.problem}</p>
      </section>
      <section className="onepager-cost-copy">
        <h2 className="onepager-heading">What it costs to wait</h2>
        <p>{onepager.costOfInaction}</p>
      </section>
      <section className="onepager-how-it-works">
        <h2 className="onepager-heading">What we will cover together</h2>
        <ol className="onepager-steps">
          {onepager.steps.map((step, index) => (
            <li key={`${step.title}-${index}`}><span>{index + 1}</span><div><strong>{step.title}</strong><p>{step.detail}</p></div></li>
          ))}
        </ol>
      </section>
      <section className="onepager-proof">
        <h2 className="onepager-heading">Why this works</h2>
        <ul className="onepager-proof-list">
          {onepager.proofPoints.map((point) => <li key={point}><Check size={14} weight="bold" />{point}</li>)}
        </ul>
      </section>
      <CustomerLogoBar segment={segment} />
      {onepager.roi && <PrecallRoiCard roi={onepager.roi} segment={segment} />}
      <section className="onepager-next-step"><h2 className="onepager-heading onepager-heading-compact">Next step</h2><p>{onepager.nextStep}</p></section>
    </div>
  )
}

function CustomerLogoBar({ segment }: { segment: PublicWorkspace['customerSegment'] }) {
  const logos = logosForSegment(segment)
  return (
    <section className="customer-logo-bar" aria-label="Prentus customers">
      <h2>Trusted by career teams</h2>
      <div className={`customer-logo-grid customer-logo-grid-${segment}`}>
        {logos.map((logo) => <img key={logo.name} src={logo.src} alt={logo.name} className={logo.forceDark ? 'customer-logo-force-dark' : undefined} />)}
      </div>
    </section>
  )
}

function PrecallRoiCard({ roi, segment }: { roi: NonNullable<NonNullable<PublicWorkspace['precallOnepager']>['roi']>; segment: PublicWorkspace['customerSegment'] }) {
  const attrition = roi.retentionRatePercent === null ? null : Math.max(0, 100 - roi.retentionRatePercent)
  const noun = houseNoun(segment)
  return (
    <section className="precall-roi" aria-label="Estimated return on investment">
      <div className="precall-roi-head">
        <h2 className="onepager-heading onepager-heading-compact">Supporting business case</h2>
        <strong>{compactMoney(roi.annualValue)}</strong>
        <p>in modeled annual value. The {noun.singular} experience comes first; this is supporting context for the investment.</p>
      </div>
      <dl>
        <div><dt>Current enrollment</dt><dd>{number(roi.enrollment)}</dd></div>
        {attrition !== null && <div><dt>Retention gap</dt><dd>{attrition.toFixed(1)}%</dd></div>}
        {roi.completionRatePercent !== null && <div><dt>Published completion</dt><dd>{roi.completionRatePercent.toFixed(1)}%</dd></div>}
        <div><dt>{noun.plural.charAt(0).toUpperCase() + noun.plural.slice(1)} retained</dt><dd>{Math.round(roi.studentsRetained)}</dd></div>
        <div><dt>Tuition retained</dt><dd>{compactMoney(roi.tuitionRetained)}</dd></div>
      </dl>
      <p className="precall-roi-assumptions">{roi.assumptions}</p>
      <div className="precall-roi-sources">Sources: {roi.sources.map((source, index) => <span key={source.url}>{index > 0 && ' · '}<a href={source.url} target="_blank" rel="noreferrer">{source.label}</a></span>)}</div>
    </section>
  )
}

export function summaryBullets(value: string): string[] {
  // Split on line breaks and inline bullet markers before collapsing
  // whitespace; collapsing first turned every newline into a space and
  // rendered a multi-line recap as one bullet.
  const explicit = value
    .split(/\r?\n|\s+[•·]\s+/)
    .map((item) => item.replace(/^\s*[-•·]\s*/, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  if (explicit.length > 1) return explicit.slice(0, 5)
  const cleaned = value.replace(/\s+/g, ' ').trim()
  if (!cleaned) return []
  return (cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned]).map((item) => item.trim()).filter(Boolean).slice(0, 5)
}

function LifecycleTab({ workspace, tab, onTrack }: { workspace: PublicWorkspace | AdminWorkspace; tab: CustomerTab; onTrack?: (eventType: string, targetId?: string) => void }) {
  const labels: Partial<Record<CustomerTab, { title: string; description: string }>> = {
    implementation: { title: 'Implementation', description: 'The launch plan, owners, milestones, and decisions for your rollout.' },
    training: { title: 'Training', description: 'Admin and advisor training resources for a confident launch.' },
    adoption: { title: 'Adoption', description: 'Engagement priorities, campaigns, and the actions that keep participation growing.' },
    outcomes: { title: 'Outcomes', description: 'Shared results, reporting resources, and evidence of progress.' },
    resources: { title: 'Resources', description: 'The product guidance and customer resources selected for your team.' },
    support: { title: 'Support', description: 'Your support contacts, service resources, and open follow-ups.' },
    'whats-new': { title: "What's New", description: 'New product launches, feature updates, and demos from Prentus.' },
  }
  const copy = labels[tab] || { title: 'Workspace', description: 'Shared resources for your partnership.' }
  const assets = lifecycleTabAssets(workspace.assets, tab)
  const empty = tab === 'whats-new' ? 'New releases and demos show up here.' : `Nothing here yet for ${copy.title}. Ask us and we will add it.`
  return (
    <section className="standard-tab">
      <SectionHeader title={copy.title} description={copy.description} />
      {tab === 'whats-new'
        ? <AssetCardGrid assets={assets} empty={empty} segment={workspace.customerSegment} onTrack={onTrack} />
        : tab === 'resources'
        ? <ResourceSections assets={assets} empty={empty} segment={workspace.customerSegment} onTrack={onTrack} />
        : <AssetRows assets={assets} empty={empty} onTrack={onTrack} />}
    </section>
  )
}

function ResourceSections({ assets, empty, segment, onTrack }: { assets: WorkspaceAsset[]; empty: string; segment: PublicWorkspace['customerSegment']; onTrack?: (eventType: string, targetId?: string) => void }) {
  if (!assets.length) return <div className="empty-state"><Clock size={22} /><p>{empty}</p></div>
  const groups = new Map<string, WorkspaceAsset[]>()
  for (const asset of assets) {
    const category = typeof asset.metadata?.category === 'string' && asset.metadata.category ? asset.metadata.category : 'More resources'
    groups.set(category, [...(groups.get(category) || []), asset])
  }
  return (
    <div className="resource-sections">
      {Array.from(groups.entries()).map(([category, items]) => (
        <section className="resource-section" key={category}>
          <h3>{applyHouseNoun(category, segment)}</h3>
          <div className="resource-row-list">
            {items.map((asset) => {
              const isVideo = asset.type === 'video' || asset.type === 'iframe'
              const image = typeof asset.metadata?.image === 'string' ? asset.metadata.image : typeof asset.metadata?.thumbnail === 'string' ? asset.metadata.thumbnail : null
              const Icon = isVideo ? PlayCircle : asset.type === 'pdf' ? File : FolderOpen
              const title = applyHouseNoun(asset.title, segment)
              const description = applyHouseNoun(asset.description || '', segment)
              const content = (
                <>
                  {image
                    ? <span className="resource-row-media"><img src={image} alt="" />{isVideo && <b><PlayCircle size={30} aria-hidden="true" /></b>}</span>
                    : <span className="resource-row-media resource-row-media-art"><Icon size={30} weight="duotone" /></span>}
                  <span className="resource-row-copy">
                    <strong>{title}</strong>
                    {description && <small>{description}</small>}
                  </span>
                  <span className="resource-tile-action">{isVideo ? 'Watch' : 'Open'}<ArrowSquareOut size={15} /></span>
                </>
              )
              return asset.url
                ? <a className="resource-row" key={asset.id} href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>{content}</a>
                : <article className="resource-row" key={asset.id}>{content}</article>
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

function AssetCardGrid({ assets, empty, segment, onTrack }: { assets: WorkspaceAsset[]; empty: string; segment?: PublicWorkspace['customerSegment']; onTrack?: (eventType: string, targetId?: string) => void }) {
  if (!assets.length) return <div className="empty-state"><Clock size={22} /><p>{empty}</p></div>
  return (
    <div className="asset-card-grid">
      {assets.map((asset) => {
        const image = typeof asset.metadata?.image === 'string' ? asset.metadata.image : typeof asset.metadata?.thumbnail === 'string' ? asset.metadata.thumbnail : null
        const isVideo = asset.type === 'video' || asset.type === 'iframe'
        const title = applyHouseNoun(asset.title, segment)
        const description = applyHouseNoun(asset.description || '', segment)
        const category = asset.metadata?.category ? applyHouseNoun(String(asset.metadata.category), segment) : null
        const content = (
          <>
            {image
              ? <span className="asset-card-media"><img src={image} alt="" />{isVideo && <b><PlayCircle size={32} aria-hidden="true" /></b>}</span>
              : <span className="asset-card-media asset-card-media-art">{isVideo ? <PlayCircle size={30} /> : asset.type === 'pdf' ? <File size={30} /> : <SquaresFour size={30} />}</span>}
            <span className="asset-card-copy">
              {category && <em>{category}</em>}
              <strong>{title}</strong>
              {description && <small>{description}</small>}
              {asset.url
                ? <span className="row-action">{isVideo ? 'Watch' : 'Open'}<ArrowSquareOut size={15} /></span>
                : <span className="coming-soon">Ask us for this one</span>}
            </span>
          </>
        )
        return asset.url
          ? <a className="asset-card" key={asset.id} href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>{content}</a>
          : <article className="asset-card" key={asset.id}>{content}</article>
      })}
    </div>
  )
}

function RecordingCards({ assets, onTrack }: { assets: WorkspaceAsset[]; onTrack?: (eventType: string, targetId?: string) => void }) {
  if (!assets.length) return <div className="empty-state"><Clock size={22} /><p>Recordings show up here after each call.</p></div>
  return (
    <div className="recording-list">
      {assets.map((asset) => {
        const thumbnail = typeof asset.metadata?.thumbnail === 'string' ? asset.metadata.thumbnail : null
        const date = metadataString(asset, ['date', 'recordedAt', 'occurredAt', 'publishedAt'])
        const participants = metadataList(asset, ['participants', 'attendees'])
        const duration = metadataString(asset, ['duration', 'durationMinutes'])
        const host = metadataString(asset, ['hostName', 'ownerName'])
        const displayDate = date ? formatRecordingDate(date) : recordingDateFromDescription(asset.description)
        const hostPhoto = metadataString(asset, ['hostAvatarUrl', 'ownerAvatarUrl']) || (host && /rod danan|rod/i.test(host) ? '/images/team/rod-danan.webp' : '/images/team/rod-danan.webp')
        const content = <><span className="recording-thumb">{thumbnail ? <img src={thumbnail} alt="" /> : <img src={hostPhoto} alt="" />}<b><PlayCircle size={24} aria-hidden="true" /></b></span><span className="recording-copy"><strong>{asset.title}</strong><span className="recording-meta">{displayDate || 'Date to confirm'}{participants.length > 0 && <> · {participants.join(', ')}</>}{duration && <> · {duration}</>}</span>{asset.description && <small>{asset.description}</small>}<em>Watch recording<ArrowSquareOut size={14} /></em></span></>
        return asset.url
          ? <a key={asset.id} className="recording-row" href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>{content}</a>
          : <article key={asset.id} className="recording-row unavailable">{content}</article>
      })}
    </div>
  )
}

function metadataString(asset: WorkspaceAsset, keys: string[]): string | null {
  for (const key of keys) {
    const value = asset.metadata?.[key]
    if (typeof value === 'string' || typeof value === 'number') return String(value)
  }
  return null
}

function metadataList(asset: WorkspaceAsset, keys: string[]): string[] {
  for (const key of keys) {
    const value = asset.metadata?.[key]
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
  }
  return []
}

export function recordingDateFromDescription(description: string | null): string | null {
  const match = description?.match(/\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}/)
  return match?.[0] || null
}

export function formatRecordingDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function sortDatedVisibleTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks
    .filter((task) => task.visibility !== 'hidden' && Boolean(task.dueDate))
    .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))
}

function AssetRows({ assets, empty, onTrack }: { assets: WorkspaceAsset[]; empty: string; onTrack?: (eventType: string, targetId?: string) => void }) {
  if (!assets.length) return <div className="empty-state"><Clock size={22} /><p>{empty}</p></div>
  return (
    <div className="asset-rows">
      {assets.map((asset) => {
        const thumbnail = typeof asset.metadata?.thumbnail === 'string' ? asset.metadata.thumbnail : null
        const content = <>{thumbnail ? <span className="asset-preview"><img src={thumbnail} alt="" /><i><PlayCircle size={30} weight="fill" /></i></span> : <span className="asset-icon">{asset.type === 'video' ? <PlayCircle size={22} /> : <File size={22} />}</span>}<div><strong>{asset.title}</strong><p>{asset.description}</p></div>{asset.url ? <span className="row-action">{asset.type === 'video' || asset.type === 'iframe' ? 'Watch' : 'Open'}<ArrowSquareOut size={15} /></span> : <span className="coming-soon">Ask us for this one</span>}</>
        return asset.url
          ? <a className={thumbnail ? 'asset-row has-preview' : 'asset-row'} key={asset.id} href={asset.url} target="_blank" rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>{content}</a>
          : <article className={thumbnail ? 'asset-row has-preview' : 'asset-row'} key={asset.id}>{content}</article>
      })}
    </div>
  )
}

function CollateralGrid({ assets, accountName, labels, onTrack }: { assets: WorkspaceAsset[]; accountName?: string; /** A short label above each title, by asset id (for example the project name). */ labels?: Record<string, string>; onTrack?: (eventType: string, targetId?: string) => void }) {
  if (!assets.length) return <div className="empty-state"><FolderOpen size={22} /><p>Materials made for {accountName || 'your team'} show up here as we add them.</p></div>
  return (
    <div className="collateral-grid">
      {assets.map((asset) => {
        const thumbnail = typeof asset.metadata?.thumbnail === 'string' ? asset.metadata.thumbnail : null
        return (
          <a key={asset.id} href={asset.url || '#'} target={asset.url ? '_blank' : undefined} rel="noreferrer" onClick={() => onTrack?.('asset_open', asset.id)}>
            {thumbnail ? <img src={thumbnail} alt="" /> : <div className="collateral-placeholder"><File size={28} /></div>}
            <span>{labels?.[asset.id] && <em>{labels[asset.id]}</em>}<strong>{asset.title}</strong><small>{asset.description}</small></span>
            <ArrowSquareOut size={17} />
          </a>
        )
      })}
    </div>
  )
}

export const WORKSPACE_CUSTOMER_TABS = SALES_TABS
